import React, { createContext, useContext, useId } from 'react';
import { cn } from '../../utils/cn';

const TONES = {
    label: 'text-fg-secondary',
    required: 'text-danger',
    hint: 'text-fg-muted',
    error: 'text-danger',
};

const FieldContext = createContext(null);

/**
 * Accessibility props for a control rendered inside a <Field>: id, aria-describedby and
 * aria-invalid. Explicit props on the control win. Outside a Field it returns the props as-is.
 */
export function useFieldControl({ id, invalid, ...rest } = {}) {
    const field = useContext(FieldContext);
    const isInvalid = Boolean(invalid ?? field?.invalid);
    return {
        id: id ?? field?.id,
        'aria-describedby': rest['aria-describedby'] ?? field?.describedBy,
        'aria-invalid': isInvalid || undefined,
        invalid: isInvalid,
    };
}

/**
 * Label + control + hint + error. The control (TextInput, Select, Textarea, NumberInput)
 * picks up htmlFor/aria-describedby/aria-invalid through context, so call sites only pass
 * the control's own props.
 *
 * @param {React.ReactNode} label
 * @param {React.ReactNode} [hint] help text under the control
 * @param {React.ReactNode} [error] error text; also marks the control aria-invalid
 * @param {boolean} [required] shows a required marker after the label
 * @param {string} [id] control id; generated when omitted
 * @param {string} [className] wrapper classes
 * @param {string} [labelClassName] overrides the default label layout classes
 */
export function Field({ label, hint, error, required, id, className, labelClassName, children }) {
    const autoId = useId();
    const controlId = id || autoId;
    const hintId = hint ? `${controlId}-hint` : undefined;
    const errorId = error ? `${controlId}-error` : undefined;
    const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

    return (
        <FieldContext.Provider value={{ id: controlId, describedBy, invalid: Boolean(error) }}>
            <div className={className}>
                {label && (
                    <label
                        htmlFor={controlId}
                        className={cn(labelClassName || 'block text-sm font-medium mb-1', TONES.label)}
                    >
                        {label}
                        {required && (
                            <span className={cn('ml-0.5', TONES.required)} aria-hidden="true">
                                *
                            </span>
                        )}
                    </label>
                )}
                {children}
                {hint && (
                    <p id={hintId} className={cn('mt-1 text-xs', TONES.hint)}>
                        {hint}
                    </p>
                )}
                {error && (
                    <p id={errorId} className={cn('mt-1 text-xs', TONES.error)}>
                        {error}
                    </p>
                )}
            </div>
        </FieldContext.Provider>
    );
}
